<template>
  <DSDialog
    :open="isOpen"
    @update:open="handleUpdateOpen"
  >
    <DSDialogContent class="gap-0">
      <DSDialogHeader class="border-b border-border">
        <div class="flex items-center gap-2">
          <DSButton
            v-if="onBack"
            variant="ghost"
            size="icon-sm"
            class="-ml-2 shrink-0"
            aria-label="Back to add asset"
            @click="handleBack"
          >
            <ArrowLeft class="size-4" />
          </DSButton>
          <DSDialogTitle class="text-xl">
            More options
          </DSDialogTitle>
        </div>
        <DSDialogDescription>
          Connect through an aggregator, or import a file your institution gives you.
        </DSDialogDescription>
      </DSDialogHeader>

      <ul class="flex flex-col gap-2.5 px-6 py-5">
        <li
          v-for="option in options"
          :key="option.label"
        >
          <button
            type="button"
            class="flex items-center gap-3.5 w-full p-3.5 text-left
              bg-card border border-border rounded-ds-md cursor-pointer
              transition-[border-color,box-shadow] duration-ds-fast ease-ds-standard
              hover:border-input hover:shadow-ds-sm
              focus-visible:outline-none focus-visible:shadow-ds-focus
              disabled:pointer-events-none disabled:opacity-50"
            :disabled="loading"
            :data-refid="option.dataRefid"
            @click="option.action"
          >
            <span
              class="inline-flex shrink-0 items-center justify-center size-10
                bg-accent text-accent-foreground rounded-ds-sm"
            >
              <component
                :is="option.icon"
                class="size-5"
              />
            </span>
            <span class="flex-1 min-w-0">
              <span class="block text-sm font-bold text-foreground">
                {{ option.label }}
              </span>
              <span class="block text-xs font-medium text-muted-foreground">
                {{ option.description }}
              </span>
            </span>
            <ArrowRight class="size-4 shrink-0 text-muted-foreground" />
          </button>
        </li>
      </ul>
    </DSDialogContent>
  </DSDialog>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, markRaw } from 'vue'

import { ModalClass } from '@types'
import { LinkedDataConnectors } from '@/views/LinkedData/utils/enums'

import { POWER_MODE } from '@/const'

import { useInstitutionConnect } from '@/hooks/institutionConnect'

import { useModalsStore } from '@/store/modals'

import { DSButton, DSDialog, DSDialogContent, DSDialogDescription, DSDialogHeader, DSDialogTitle } from '@ds'
import { ArrowLeft, ArrowRight, FileUp, Landmark, Network } from '@lucide/vue'
import ConnectorOFX from './ConnectorOFX.vue'

/*
 * The connector options behind "More options" in the Add asset dialog.
 *
 * Small enough to be its own adapter: unlike AddAsset, there is no reusable @ds
 * screen underneath worth keeping free of ModalClass, so the modal contract and
 * the markup live in one file.
 */
type Props = {
  modal: ModalClass<any>
  /**
   * Return to whatever opened this. Declared as a prop rather than an emit so
   * the template can ask whether a previous step exists — an emit named `back`
   * would take `onBack` out of props and leave nothing to test.
   */
  onBack?: () => void
  /** End the whole flow, including the caller that hid itself to open this. */
  onDismiss?: () => void
}

const props = defineProps<Props>()

const modalsStore = useModalsStore()

const isPowerMode = inject<ComputedRef<boolean>>(POWER_MODE)

const isOpen = computed(() => props.modal.isOpened)

/*
 * hide() rather than close(): modalsStore removes a modal from its list on
 * CLOSE, so a closed dialog cannot be shown again. See AddAssetDialog for the
 * full note.
 */
const hide = () => props.modal.hide()
const show = () => props.modal.show(modalsStore.getZIndex())

const { loading, connectWithPlaid, connectWithYodlee } = useInstitutionConnect({
  hide,
  show,
})

/*
 * Back steps up one level; Esc, the scrim and the close button end the flow.
 * They have to be distinguishable, because the caller is HIDDEN rather than
 * closed — dismissing without telling it would strand a hidden modal in the
 * store that nothing can reach.
 */
const handleBack = () => {
  props.modal.close()
  props.onBack?.()
}

const handleUpdateOpen = (value: boolean) => {
  if (value) return
  props.modal.close()
  props.onDismiss?.()
}

const handleOFX = () => {
  hide()
  const modalInstance = modalsStore.init(
    'add-account-ofx-dialog',
    markRaw(ConnectorOFX),
  )
  modalInstance?.open(modalsStore.getZIndex(), {
    onBack: show,
    onCancel: show,
  })
}

/*
 * Plaid and Yodlee stay behind power mode, as they are in the legacy connect
 * dialog — picking an aggregator directly is a power-user action, and everyone
 * else reaches the same institutions through the grid.
 */
const options = computed(() => [
  ...(isPowerMode?.value
    ? [
      {
        icon: Landmark,
        label: `Use ${LinkedDataConnectors.PLAID} connector`,
        description: 'Link an account through Plaid.',
        action: () => connectWithPlaid(),
      },
      {
        icon: Network,
        label: `Use ${LinkedDataConnectors.YODLEE} connector`,
        description: 'Link an account through Yodlee FastLink.',
        action: () => connectWithYodlee(),
      },
    ]
    : []),
  {
    icon: FileUp,
    label: `Use ${LinkedDataConnectors.OFX} connector`,
    description: 'Import a .ofx or .qfx file exported from your institution.',
    dataRefid: 'useOfxConnector',
    action: handleOFX,
  },
])
</script>
